/**
 * ReconnectionBanner.tsx
 * Dismissible reconnection banner for sync provider failures
 *
 * Features:
 * - 24h snooze with TTL-based localStorage persistence
 * - Auto-retry on online event (even when dismissed - snooze only affects UI)
 * - Rate-limit countdown with visual feedback
 * - Success animation (3s) before auto-close
 * - Fully accessible (role="status", keyboard navigation, touch targets)
 * - Mobile responsive (vertical stack on mobile, inline on desktop)
 * - Self-contained state management
 *
 * Usage:
 * Parent component conditionally renders this banner based on connection state.
 * Banner handles reconnection logic internally via useProviderConnection hook.
 *
 * @example
 * ```tsx
 * {!isCloudConnected && userSettings.activeSyncProvider === 'google-drive' && (
 *   <ReconnectionBanner
 *     userId={user.id}
 *     providerKey="google-drive"
 *     onDismiss={() => console.log('Banner dismissed')}
 *     onSuccess={() => showToast({ title: 'Reconnected!' })}
 *   />
 * )}
 * ```
 */

import React, { useState, useEffect, useCallback, useRef, useMemo, useId } from 'react';
import { WifiOff, RefreshCw, CheckCircle, Clock } from 'lucide-react';

// UI Components
import { Alert } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';

// Hooks
import { useSyncConnection } from '@/contexts';

// Types
import type { ProviderKey } from '@/types/sync';

// Utilities
import {
  isBannerDismissed,
  dismissBanner,
  clearBannerDismissal,
  getSyncErrorMessage,
  openOAuthPopup,
  syncInitReasonToErrorCode,
} from '@/utils';
import { getProviderDisplayName, requiresConnectivity } from '@/utils/syncProviderUtils';
import { isOnline } from '@/utils/connectionUtils';

/**
 * Props for ReconnectionBanner component
 */
export interface ReconnectionBannerProps {
  /** User ID for snooze persistence */
  userId: string;
  /** Provider key (e.g., 'google-drive', 'local') */
  providerKey: ProviderKey;
  /** Callback when banner is dismissed by user */
  onDismiss?: () => void;
  /** Callback when reconnection succeeds */
  onSuccess?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Dismissible banner for prompting sync provider reconnection.
 *
 * Self-contained component with:
 * - TTL-based dismissal tracking (24h snooze in localStorage)
 * - Auto-retry on online events (even when snoozed - only affects UI visibility)
 * - Rate-limit countdown with per-second updates
 * - Success feedback (3s) before auto-close
 * - Full accessibility (role="status", keyboard navigation, ≥44px touch targets)
 * - Mobile responsive layout (stacks vertically < 768px)
 *
 * Parent controls visibility by conditionally rendering based on connection state.
 * Banner manages reconnection logic internally via useProviderConnection hook.
 */
export const ReconnectionBanner: React.FC<ReconnectionBannerProps> = ({
  userId,
  providerKey,
  onDismiss,
  onSuccess,
  className,
}) => {
  // Connection hook
  const { state, connect, abort } = useSyncConnection();

  // Unique ID for aria-describedby
  const messageId = useId();

  // Local state
  const [isDismissed, setIsDismissed] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);

  // Refs for cleanup
  const successTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalId = useRef<ReturnType<typeof setInterval> | null>(null);
  const onlineDebounceTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track if this banner instance initiated the current connection
  // Only abort on unmount if we started the connection (don't abort other components' connections)
  const initiatedConnectionRef = useRef(false);

  // Check if banner is snoozed (24h TTL)
  const isSnoozed = useMemo(
    () => isBannerDismissed(userId, providerKey),
    [userId, providerKey]
  );

  // Provider display name
  const providerDisplayName = useMemo(
    () => getProviderDisplayName(providerKey),
    [providerKey]
  );

  // Get error message for display
  const errorMessage = useMemo((): string | null => {
    // Only show error messages for states with a reason
    if (state.status !== 'needsReauth' && state.status !== 'error') {
      return null;
    }

    // Get error message using adapter + utility (matches ReconnectionModal pattern)
    const errorCode = syncInitReasonToErrorCode(state.reason);
    const syncError = {
      code: errorCode,
      message: state.error,
      provider: providerKey === 'google-drive' ? ('google-drive' as const) : ('local' as const),
    };
    const messageObj = getSyncErrorMessage(syncError, 'banner', providerDisplayName);
    return messageObj.description;
  }, [state, providerKey, providerDisplayName]);

  /**
   * Handle reconnect button click
   *
   * For google-drive: Opens OAuth popup synchronously to preserve user gesture context.
   * If popup is blocked, shows error message and bails early.
   *
   * For local-directory: No popup needed, proceeds directly to connect.
   */
  const handleReconnect = useCallback((): void => {
    // Re-entrancy guards
    if (state.status === 'connecting' || state.status === 'remoteConnecting' || state.status === 'success') {
      return;
    }

    // Open popup synchronously (returns null for non-google-drive providers)
    const popup = openOAuthPopup(providerKey);

    // Guard for google-drive: if popup was blocked, show message and bail
    if (providerKey === 'google-drive' && !popup) {
      setPopupBlocked(true);
      return;
    }

    // Clear popup blocked state on successful popup open (for google-drive)
    if (providerKey === 'google-drive' && popup) {
      setPopupBlocked(false);
    }

    // Mark that this banner instance initiated the connection
    // This allows cleanup to know whether to abort (only abort our own connections)
    initiatedConnectionRef.current = true;

    // Initiate connection with popup reference
    // Note: This works for all states including needsReauth
    // - For OAuth providers (Google Drive), connect() triggers OAuth flow
    // - For non-OAuth providers (Local Directory), connect() triggers File System API
    // - The modal/banner visibility logic in PrivateAppProvider prevents concurrent attempts
    connect(providerKey, { oauthPopup: popup });

    // TODO Phase 7: telemetry.track('sync_banner_reconnect', { provider, reason })
  }, [connect, state.status, providerKey]);

  /**
   * Handle dismiss button click
   */
  const handleDismiss = useCallback((): void => {
    setIsDismissed(true);
    dismissBanner(userId, providerKey);
    onDismiss?.();

    // TODO Phase 7: telemetry.track('sync_banner_dismissed', { provider, reason })
  }, [userId, providerKey, onDismiss]);

  /**
   * Auto-close on success (3s delay)
   */
  useEffect(() => {
    if (state.status === 'success') {
      setShowSuccessMessage(true);
      onSuccess?.();

      // Clear snooze so banner can reappear if needed in future
      clearBannerDismissal(userId, providerKey);

      // Auto-close after 3s
      successTimeoutId.current = setTimeout(() => {
        setIsDismissed(true);
      }, 3000);

      // TODO Phase 7: telemetry.track('sync_banner_success', { provider: providerKey })

      // Cleanup on unmount or state change
      return () => {
        if (successTimeoutId.current) {
          clearTimeout(successTimeoutId.current);
          successTimeoutId.current = null;
        }
      };
    }
    return undefined;
  }, [state.status, userId, providerKey, onSuccess]);

  /**
   * Rate-limit countdown timer
   * Extract retryAfterMs for ESLint dependency tracking
   */
  const retryAfterMs = state.status === 'rateLimited' ? state.retryAfterMs : null;

  useEffect(() => {
    if (state.status === 'rateLimited' && retryAfterMs !== null) {
      // Initialize countdown from retryAfterMs
      setCountdown(Math.ceil(retryAfterMs / 1000));

      // Update every second
      countdownIntervalId.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev === null || prev <= 1) {
            // Countdown complete
            if (countdownIntervalId.current) {
              clearInterval(countdownIntervalId.current);
              countdownIntervalId.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Cleanup
      return () => {
        if (countdownIntervalId.current) {
          clearInterval(countdownIntervalId.current);
          countdownIntervalId.current = null;
        }
      };
    } else {
      // Clear countdown if not rate-limited
      setCountdown(null);
    }
    return undefined;
  }, [state.status, retryAfterMs]);

  /**
   * Auto-retry on online event (with 250ms debounce to prevent duplicate firings)
   */
  useEffect(() => {
    // SSR guard
    if (typeof window === 'undefined') {
      return;
    }

    const handleOnline = (): void => {
      // Early exit: Local Directory doesn't need auto-retry on online events
      // (File System API works offline - connectivity is irrelevant)
      if (!requiresConnectivity(providerKey)) {
        return;
      }

      // Early exit: Google Drive requires OAuth popup (user gesture) - can't auto-retry
      // User must click the explicit Reconnect button which opens popup synchronously
      if (providerKey === 'google-drive') {
        return;
      }

      // Clear any pending debounce
      if (onlineDebounceTimeoutId.current) {
        clearTimeout(onlineDebounceTimeoutId.current);
      }

      // Debounce for 250ms to prevent duplicate 'online' events in some browsers
      onlineDebounceTimeoutId.current = setTimeout(() => {
        // Guards before auto-retry
        if (!isOnline()) return; // Redundant safety check
        if (state.status === 'connecting') return;
        if (state.status === 'success') return;
        if (state.status === 'needsReauth') return; // Modal case - don't loop

        // Only auto-retry from retriable states
        if (
          state.status === 'offline' ||
          state.status === 'error' ||
          (state.status === 'rateLimited' && countdown === 0)
        ) {
          void connect(providerKey);
          // TODO Phase 7: telemetry.track('sync_banner_auto_retry', { provider: providerKey, trigger: 'online' })
        }
      }, 250);
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
      // Clear debounce on cleanup
      if (onlineDebounceTimeoutId.current) {
        clearTimeout(onlineDebounceTimeoutId.current);
        onlineDebounceTimeoutId.current = null;
      }
    };
  }, [connect, state.status, providerKey, countdown]);

  /**
   * Reset connection ownership when connection completes
   * This ensures the ref is ready for the next connection attempt
   */
  useEffect(() => {
    if (state.status === 'idle' || state.status === 'success') {
      initiatedConnectionRef.current = false;
    }
  }, [state.status]);

  /**
   * Reset popupBlocked when state transitions to non-actionable states
   * This clears stale warning when connection succeeds or is in progress
   */
  useEffect(() => {
    if (state.status === 'success' || state.status === 'connecting') {
      setPopupBlocked(false);
    }
  }, [state.status]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      // Clear all timers
      if (successTimeoutId.current) {
        clearTimeout(successTimeoutId.current);
      }
      if (countdownIntervalId.current) {
        clearInterval(countdownIntervalId.current);
      }
      if (onlineDebounceTimeoutId.current) {
        clearTimeout(onlineDebounceTimeoutId.current);
      }
      // Only abort if THIS banner initiated the connection
      // Don't abort connections started by other components (e.g., DataSyncSettings)
      if (initiatedConnectionRef.current) {
        abort();
      }
    };
  }, [abort]);

  // Visibility logic - early return AFTER all hooks
  const shouldShow = !isDismissed && !isSnoozed;
  if (!shouldShow) {
    return null;
  }

  /**
   * Render banner based on state
   */
  const renderContent = (): React.ReactNode => {
    // Success state
    if (showSuccessMessage && state.status === 'success') {
      return (
        <div className="flex items-center gap-2">
          <CheckCircle className="size-5 text-success" />
          <span className="font-medium">Reconnected successfully!</span>
        </div>
      );
    }

    // Connecting state
    if (state.status === 'connecting') {
      return (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <RefreshCw className="size-5 animate-spin" />
            <span>Reconnecting to {providerDisplayName}...</span>
          </div>
          <Button
            color="default"
            size="sm"
            variant="flat"
            onPress={abort}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
        </div>
      );
    }

    // Remote connecting state (another tab is reconnecting)
    if (state.status === 'remoteConnecting') {
      return (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <RefreshCw className="size-5 animate-spin" />
            <span>Another tab is reconnecting to {providerDisplayName}...</span>
          </div>
          <Button
            color="primary"
            size="sm"
            variant="solid"
            isDisabled={true}
            className="w-full sm:w-auto min-h-[44px]"
          >
            Reconnect
          </Button>
        </div>
      );
    }

    // Offline state
    if (state.status === 'offline') {
      return (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <WifiOff className="size-5" />
            <span id={messageId}>You're offline. Reconnection will be attempted automatically when you're back online.</span>
          </div>
          <div className="flex flex-col gap-1">
            <Button
              color="primary"
              size="sm"
              variant="solid"
              onPress={handleReconnect}
              aria-describedby={messageId}
              className="w-full sm:w-auto min-h-[44px]"
            >
              Reconnect to {providerDisplayName}
            </Button>
            {popupBlocked && (
              <span className="text-xs text-warning-600 dark:text-warning-400">
                Please allow popups for this site and try again.
              </span>
            )}
          </div>
        </div>
      );
    }

    // Rate-limited state
    if (state.status === 'rateLimited') {
      return (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Clock className="size-5" />
            <span id={messageId}>
              Rate limited. Try again in {countdown !== null ? countdown : '?'}s.
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <Button
              color="primary"
              size="sm"
              variant="solid"
              onPress={handleReconnect}
              isDisabled={countdown === null || countdown > 0}
              aria-describedby={messageId}
              className="w-full sm:w-auto min-h-[44px]"
            >
              {countdown !== null && countdown > 0 ? `Retry (${countdown}s)` : `Reconnect to ${providerDisplayName}`}
            </Button>
            {popupBlocked && (
              <span className="text-xs text-warning-600 dark:text-warning-400">
                Please allow popups for this site and try again.
              </span>
            )}
          </div>
        </div>
      );
    }

    // NeedsReauth state
    if (state.status === 'needsReauth') {
      return (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col gap-1" id={messageId}>
            <span className="font-medium">Access expired. Reconnect to {providerDisplayName}.</span>
            {errorMessage && <span className="text-sm opacity-80">{errorMessage}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Button
              color="primary"
              size="sm"
              variant="solid"
              onPress={handleReconnect}
              aria-describedby={messageId}
              className="w-full sm:w-auto min-h-[44px]"
            >
              Reconnect to {providerDisplayName}
            </Button>
            {popupBlocked && (
              <span className="text-xs text-warning-600 dark:text-warning-400">
                Please allow popups for this site and try again.
              </span>
            )}
          </div>
        </div>
      );
    }

    // Error state
    if (state.status === 'error') {
      return (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-col gap-1" id={messageId}>
            <span className="font-medium">
              Unable to connect to {providerDisplayName}.
            </span>
            {errorMessage && <span className="text-sm opacity-80">{errorMessage}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <Button
              color="primary"
              size="sm"
              variant="solid"
              onPress={handleReconnect}
              aria-describedby={messageId}
              className="w-full sm:w-auto min-h-[44px]"
            >
              Reconnect to {providerDisplayName}
            </Button>
            {popupBlocked && (
              <span className="text-xs text-warning-600 dark:text-warning-400">
                Please allow popups for this site and try again.
              </span>
            )}
          </div>
        </div>
      );
    }

    // Idle state (fallback)
    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-col gap-1" id={messageId}>
          <span className="font-medium">
            {providerDisplayName} disconnected.
          </span>
          {errorMessage && <span className="text-sm opacity-80">{errorMessage}</span>}
        </div>
        <div className="flex flex-col gap-1">
          <Button
            color="primary"
            size="sm"
            variant="solid"
            onPress={handleReconnect}
            aria-describedby={messageId}
            className="w-full sm:w-auto min-h-[44px]"
          >
            Reconnect to {providerDisplayName}
          </Button>
          {popupBlocked && (
            <span className="text-xs text-warning-600 dark:text-warning-400">
              Please allow popups for this site and try again.
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Alert
      color="warning"
      variant="bordered"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      isClosable
      onClose={handleDismiss}
      {...(className ? { className } : {})}
    >
      {renderContent()}
    </Alert>
  );
};

export default ReconnectionBanner;
